import { useEffect, useRef } from 'react';

import { useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import useContractsSelectionStore from '@/data/stores/contractsSelection.store';
import useDocumentMappingSelectionStore from '@/data/stores/documentMappingSelection.store';
import useFavoritesStore, {
  type ClientFavorite,
  type ContractsFavorite,
  type DocumentMappingFavorite,
  type FavoriteSearch,
  type HeadingClientsFavorite,
  type OrdersFavorite,
  type ResourcesFavorite,
} from '@/data/stores/favorites.store';
import useHeadingClientsSelectionStore from '@/data/stores/headingClientsSelection.store';
import useOrdersSelectionStore from '@/data/stores/ordersSelection.store';
import usePixidSelectionStore from '@/data/stores/pixidSelection.store';
import useResourcesSelectionStore from '@/data/stores/resourcesSelection.store';
import { areFiltersEqual } from '@/utils/filters.utils';

export interface FavoriteSection {
  type: FavoriteSearch['type'];
  titleKey: string;
  items: FavoriteSearch[];
}

export interface FavoriteSummary {
  /** Titre affiché (tronqué). */
  title: string;
  /** Sous-titre affiché (tronqué) ; vide quand il répéterait le titre. */
  subtitle: string;
  /** Texte complet, non tronqué, pour l'infobulle native de la ligne. */
  fullText: string;
}

export interface UseFavoritesNavigationResult {
  favorites: FavoriteSearch[];
  sections: FavoriteSection[];
  isSelected: (favorite: FavoriteSearch) => boolean;
  hasSelectedFavorite: boolean;
  selectFavorite: (favorite: FavoriteSearch) => void;
  removeFavorite: (id: string) => void;
  getSummary: (favorite: FavoriteSearch) => FavoriteSummary;
}

const SECTION_TITLE_KEYS: Record<FavoriteSearch['type'], string> = {
  client: 'dashboard.pixid.favorites.section_clients',
  contracts: 'dashboard.pixid.favorites.section_contracts',
  orders: 'dashboard.pixid.favorites.section_orders',
  resources: 'dashboard.pixid.favorites.section_resources',
  heading_clients: 'dashboard.pixid.favorites.section_heading_clients',
  document_mapping: 'dashboard.pixid.favorites.section_document_mapping',
};

const truncateText = (text: string, maxLength: number = 40): string => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Favoris de l'en-tête : sections à afficher, favori actif, navigation pré-remplie
 * vers le tableau de bord du favori et remise à zéro des sélections au changement de page.
 */
export const useFavoritesNavigation = (): UseFavoritesNavigationResult => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    getClientFavorites,
    getContractsFavorites,
    getOrdersFavorites,
    getResourcesFavorites,
    getHeadingClientsFavorites,
    getDocumentMappingFavorites,
    removeFavorite,
    favorites,
  } = useFavoritesStore();
  const clientFavorites = getClientFavorites();
  const contractsFavorites = getContractsFavorites();
  const ordersFavorites = getOrdersFavorites();
  const resourcesFavorites = getResourcesFavorites();
  const headingClientsFavorites = getHeadingClientsFavorites();
  const documentMappingFavorites = getDocumentMappingFavorites();

  const {
    setPrefilledSelection,
    currentPlatform: currentClientPlatform,
    currentTenant,
    currentAgency,
    currentClient,
    clearCurrentSelection: clearClientSelection,
  } = usePixidSelectionStore();
  const {
    setPrefilledSelection: setContractsPrefilledSelection,
    setPrefilledContractPlatform,
    currentPlatform: currentContractsPlatform,
    currentTenant: currentContractsTenant,
    currentFilters: currentContractsFilters,
    clearCurrentSelection: clearContractsSelection,
  } = useContractsSelectionStore();
  const {
    setPrefilledTenant: setOrdersPrefilledTenant,
    setPrefilledFilters: setOrdersPrefilledFilters,
    setPrefilledOrderPlatform,
    currentPlatform: currentOrdersPlatform,
    currentTenant: currentOrdersTenant,
    currentFilters: currentOrdersFilters,
    clearCurrentSelection: clearOrdersSelection,
  } = useOrdersSelectionStore();
  const {
    setPrefilledSelection: setResourcesPrefilledSelection,
    setPrefilledResourcePlatform,
    currentPlatform: currentResourcesPlatform,
    currentTenant: currentResourcesTenant,
    currentFilters: currentResourcesFilters,
    clearCurrentSelection: clearResourcesSelection,
  } = useResourcesSelectionStore();
  const {
    setPrefilledSelection: setHeadingClientsPrefilledSelection,
    setPrefilledHeadingClientPlatform,
    currentPlatform: currentHeadingClientsPlatform,
    currentFilters: currentHeadingClientsFilters,
    clearCurrentSelection: clearHeadingClientsSelection,
  } = useHeadingClientsSelectionStore();
  const {
    setPrefilledSelection: setDocumentMappingPrefilledSelection,
    setPrefilledDocumentMappingPlatform,
    currentPlatform: currentDocumentMappingPlatform,
    currentFilters: currentDocumentMappingFilters,
    clearCurrentSelection: clearDocumentMappingSelection,
  } = useDocumentMappingSelectionStore();

  // Track route changes and clear selections when navigating to a different page
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      // Clear all current selections when route changes
      clearClientSelection();
      clearContractsSelection();
      clearOrdersSelection();
      clearResourcesSelection();
      clearHeadingClientsSelection();
      clearDocumentMappingSelection();
      previousPathRef.current = location.pathname;
    }
  }, [
    location.pathname,
    clearClientSelection,
    clearContractsSelection,
    clearOrdersSelection,
    clearResourcesSelection,
    clearHeadingClientsSelection,
    clearDocumentMappingSelection,
  ]);

  const isClientCurrentlySelected = (favorite: ClientFavorite) => {
    if (!currentClientPlatform || !currentTenant || !currentAgency || !currentClient)
      return false;
    return (
      favorite.platform?.value === currentClientPlatform.value &&
      favorite.tenant?.value === currentTenant.value &&
      favorite.agency?.code_agence === currentAgency.code_agence &&
      favorite.client?.id === currentClient.id
    );
  };

  const isContractsCurrentlySelected = (favorite: ContractsFavorite) => {
    if (!currentContractsPlatform || !currentContractsFilters) return false;

    // Platform must match
    if (favorite.platform?.value !== currentContractsPlatform.value) {
      return false;
    }

    // Tenant must match exactly (both have same value or both are null)
    const currentTenantValue = currentContractsTenant?.value || null;
    const favoriteTenantValue = favorite.tenant?.value || null;
    if (currentTenantValue !== favoriteTenantValue) {
      return false;
    }

    // Compare filters excluding sent_by (tenant is compared separately)
    const { sent_by: _currentSentBy, ...currentFiltersWithoutTenant } = currentContractsFilters;
    const { sent_by: _favoriteSentBy, ...favoriteFiltersWithoutTenant } = favorite.filters;

    return areFiltersEqual(currentFiltersWithoutTenant, favoriteFiltersWithoutTenant);
  };

  const isOrdersCurrentlySelected = (favorite: OrdersFavorite) => {
    if (!currentOrdersPlatform || !currentOrdersFilters) return false;

    // Platform must match
    if (favorite.platform?.value !== currentOrdersPlatform.value) {
      return false;
    }

    // Tenant must match exactly (both have same value or both are null)
    const currentTenantValue = currentOrdersTenant?.value || null;
    const favoriteTenantValue = favorite.tenant?.value || null;
    if (currentTenantValue !== favoriteTenantValue) {
      return false;
    }

    // Compare filters excluding sent_by (tenant is compared separately)
    const { sent_by: _currentSentBy, ...currentFiltersWithoutTenant } = currentOrdersFilters;
    const { sent_by: _favoriteSentBy, ...favoriteFiltersWithoutTenant } = favorite.filters;

    return areFiltersEqual(currentFiltersWithoutTenant, favoriteFiltersWithoutTenant);
  };

  const isResourcesCurrentlySelected = (favorite: ResourcesFavorite) => {
    if (!currentResourcesPlatform || !currentResourcesFilters) return false;

    // Platform must match
    if (favorite.platform?.value !== currentResourcesPlatform.value) {
      return false;
    }

    // Tenant must match exactly (both have same value or both are null)
    const currentTenantValue = currentResourcesTenant?.value || null;
    const favoriteTenantValue = favorite.tenant?.value || null;
    if (currentTenantValue !== favoriteTenantValue) {
      return false;
    }

    // Compare filters excluding sent_by (tenant is compared separately)
    const { sent_by: _currentSentBy, ...currentFiltersWithoutTenant } = currentResourcesFilters;
    const { sent_by: _favoriteSentBy, ...favoriteFiltersWithoutTenant } = favorite.filters;

    return areFiltersEqual(currentFiltersWithoutTenant, favoriteFiltersWithoutTenant);
  };

  const isHeadingClientsCurrentlySelected = (favorite: HeadingClientsFavorite) => {
    if (!currentHeadingClientsPlatform || !currentHeadingClientsFilters) return false;

    // Platform must match
    if (favorite.platform?.value !== currentHeadingClientsPlatform.value) {
      return false;
    }

    return areFiltersEqual(
      favorite.filters as Record<string, unknown>,
      currentHeadingClientsFilters as Record<string, unknown>,
    );
  };

  const isDocumentMappingCurrentlySelected = (favorite: DocumentMappingFavorite) => {
    if (!currentDocumentMappingPlatform || !currentDocumentMappingFilters) return false;

    // Platform must match
    if (favorite.platform?.value !== currentDocumentMappingPlatform.value) {
      return false;
    }

    return areFiltersEqual(
      favorite.filters as Record<string, unknown>,
      currentDocumentMappingFilters as Record<string, unknown>,
    );
  };

  const isSelected = (favorite: FavoriteSearch): boolean => {
    switch (favorite.type) {
      case 'client':
        return isClientCurrentlySelected(favorite);
      case 'contracts':
        return isContractsCurrentlySelected(favorite);
      case 'orders':
        return isOrdersCurrentlySelected(favorite);
      case 'resources':
        return isResourcesCurrentlySelected(favorite);
      case 'heading_clients':
        return isHeadingClientsCurrentlySelected(favorite);
      case 'document_mapping':
        return isDocumentMappingCurrentlySelected(favorite);
    }
  };

  const handleSelectClientFavorite = (favorite: ClientFavorite) => {
    clearContractsSelection();
    clearOrdersSelection();
    clearResourcesSelection();
    clearHeadingClientsSelection();
    setPrefilledSelection(favorite.platform, favorite.tenant, favorite.agency, favorite.client);
    navigate({ to: '/pixid/param-demat' });
  };

  const handleSelectContractsFavorite = (favorite: ContractsFavorite) => {
    clearClientSelection();
    clearOrdersSelection();
    clearResourcesSelection();
    clearHeadingClientsSelection();
    setContractsPrefilledSelection(favorite.platform, favorite.tenant, favorite.filters);
    setPrefilledContractPlatform(favorite.platform?.value);
    navigate({ to: '/contrats' });
  };

  const handleSelectOrdersFavorite = (favorite: OrdersFavorite) => {
    clearClientSelection();
    clearContractsSelection();
    clearResourcesSelection();
    clearHeadingClientsSelection();
    setOrdersPrefilledTenant(favorite.tenant);
    setOrdersPrefilledFilters(favorite.filters);
    setPrefilledOrderPlatform(favorite.platform?.value);
    navigate({ to: '/commandes' });
  };

  const handleSelectResourcesFavorite = (favorite: ResourcesFavorite) => {
    clearClientSelection();
    clearContractsSelection();
    clearOrdersSelection();
    clearHeadingClientsSelection();
    setResourcesPrefilledSelection(favorite.platform, favorite.tenant, favorite.filters);
    setPrefilledResourcePlatform(favorite.platform?.value);
    navigate({ to: '/interimaires' });
  };

  const handleSelectHeadingClientsFavorite = (favorite: HeadingClientsFavorite) => {
    clearClientSelection();
    clearContractsSelection();
    clearOrdersSelection();
    clearResourcesSelection();
    setHeadingClientsPrefilledSelection(favorite.platform, favorite.filters);
    setPrefilledHeadingClientPlatform(favorite.platform?.value);
    navigate({ to: '/config/rubriques-clients' });
  };

  const handleSelectDocumentMappingFavorite = (favorite: DocumentMappingFavorite) => {
    clearClientSelection();
    clearContractsSelection();
    clearOrdersSelection();
    clearResourcesSelection();
    clearHeadingClientsSelection();
    setDocumentMappingPrefilledSelection(favorite.platform, favorite.filters);
    setPrefilledDocumentMappingPlatform(favorite.platform?.value);
    navigate({ to: '/config/document-mapping' });
  };

  const selectFavorite = (favorite: FavoriteSearch) => {
    switch (favorite.type) {
      case 'client':
        handleSelectClientFavorite(favorite);
        break;
      case 'contracts':
        handleSelectContractsFavorite(favorite);
        break;
      case 'orders':
        handleSelectOrdersFavorite(favorite);
        break;
      case 'resources':
        handleSelectResourcesFavorite(favorite);
        break;
      case 'heading_clients':
        handleSelectHeadingClientsFavorite(favorite);
        break;
      case 'document_mapping':
        handleSelectDocumentMappingFavorite(favorite);
        break;
    }
  };

  const getClientSummary = (favorite: ClientFavorite) => {
    const parts = [];
    if (favorite.agency)
      parts.push(`${favorite.agency.code_agence} - ${favorite.agency.raison_sociale}`);
    if (favorite.client?.legal_id) parts.push(favorite.client.legal_id);
    return parts.join(' • ');
  };

  const getContractsFiltersSummary = (favorite: ContractsFavorite) => {
    const filterParts = [];
    if (favorite.platform?.value) filterParts.push(favorite.platform?.value);

    if (favorite.filters.reference) filterParts.push(favorite.filters.reference);
    if (favorite.filters.status)
      filterParts.push(t(`dashboard.contracts.status.${favorite.filters.status}`));
    if (favorite.filters.agency_code) filterParts.push(favorite.filters.agency_code);
    return filterParts.length > 0
      ? filterParts.join(' • ')
      : t('dashboard.contracts.favorites.active_filters');
  };

  const getOrdersFiltersSummary = (favorite: OrdersFavorite) => {
    const filterParts = [];

    if (favorite.platform?.value) filterParts.push(favorite.platform?.value);
    if (favorite.filters.agency_code) filterParts.push(favorite.filters.agency_code);
    if (favorite.filters.created_at)
      filterParts.push(new Date(favorite.filters.created_at).toLocaleDateString('fr-FR'));
    if (favorite.filters.reference) filterParts.push(favorite.filters.reference);
    return filterParts.length > 0
      ? filterParts.join(' • ')
      : t('dashboard.orders.favorites.active_filters');
  };

  const getResourcesFiltersSummary = (favorite: ResourcesFavorite) => {
    const filterParts = [];

    if (favorite.platform?.value) filterParts.push(favorite.platform?.value);
    if (favorite.filters.firstname) filterParts.push(favorite.filters.firstname);
    if (favorite.filters.lastname) filterParts.push(favorite.filters.lastname);
    if (favorite.filters.email) filterParts.push(favorite.filters.email);
    if (favorite.filters.created_at)
      filterParts.push(new Date(favorite.filters.created_at).toLocaleDateString('fr-FR'));
    return filterParts.length > 0
      ? filterParts.join(' • ')
      : t('dashboard.resources.favorites.active_filters');
  };

  const getHeadingClientsFiltersSummary = (favorite: HeadingClientsFavorite) => {
    const filterParts = [];
    if (favorite.platform?.value) filterParts.push(favorite.platform?.value);
    if (favorite.filters.code) filterParts.push(favorite.filters.code);
    if (favorite.filters.anael_value) filterParts.push(favorite.filters.anael_value);
    return filterParts.length > 0
      ? filterParts.join(' • ')
      : t('dashboard.heading_clients.favorites.active_filters');
  };

  const getDocumentMappingFiltersSummary = (favorite: DocumentMappingFavorite) => {
    const filterParts = [];
    if (favorite.platform?.value) filterParts.push(favorite.platform?.value);
    if (favorite.filters.code) filterParts.push(favorite.filters.code);
    if (favorite.filters.code_vms) filterParts.push(favorite.filters.code_vms);
    if (favorite.filters.label) filterParts.push(favorite.filters.label);
    return filterParts.length > 0
      ? filterParts.join(' • ')
      : t('dashboard.document_mapping.favorites.active_filters');
  };

  const getFiltersSummary = (favorite: FavoriteSearch): string => {
    switch (favorite.type) {
      case 'client':
        return getClientSummary(favorite);
      case 'contracts':
        return getContractsFiltersSummary(favorite);
      case 'orders':
        return getOrdersFiltersSummary(favorite);
      case 'resources':
        return getResourcesFiltersSummary(favorite);
      case 'heading_clients':
        return getHeadingClientsFiltersSummary(favorite);
      case 'document_mapping':
        return getDocumentMappingFiltersSummary(favorite);
    }
  };

  const getSummary = (favorite: FavoriteSearch): FavoriteSummary => {
    const summary = getFiltersSummary(favorite);
    const title =
      favorite.type === 'client' ? favorite.client?.name || '' : favorite.displayName || summary;
    const subtitle = summary === title ? '' : summary;
    return {
      title: truncateText(title),
      subtitle: truncateText(subtitle),
      fullText: subtitle ? `${title} — ${subtitle}` : title,
    };
  };

  const sections: FavoriteSection[] = [
    { type: 'client' as const, items: clientFavorites },
    { type: 'contracts' as const, items: contractsFavorites },
    { type: 'orders' as const, items: ordersFavorites },
    { type: 'resources' as const, items: resourcesFavorites },
    { type: 'heading_clients' as const, items: headingClientsFavorites },
    { type: 'document_mapping' as const, items: documentMappingFavorites },
  ]
    .filter((section) => section.items.length > 0)
    .map((section) => ({ ...section, titleKey: SECTION_TITLE_KEYS[section.type] }));

  // Check if any favorite is currently selected
  const hasSelectedFavorite = favorites.some(isSelected);

  return {
    favorites,
    sections,
    isSelected,
    hasSelectedFavorite,
    selectFavorite,
    removeFavorite,
    getSummary,
  };
};
